import React from 'react';
import { Box } from '@mui/material';
import LayerSelection from '../LayerSelection';
import { LayerOption } from '../../../types/taxonomy.types';

interface LayerSelectionStepProps {
  // Current selection
  selectedLayerCode?: string;

  // Callbacks
  onLayerSelect: (layer: LayerOption, isDoubleClick?: boolean) => void;

  // Optional props
  disabled?: boolean;
  error?: string;
}

/**
 * Step 1: Layer Selection
 *
 * This component wraps the existing LayerSelection component and provides
 * a clean interface for the refactored RegisterAssetPage. It handles:
 * - Layer grid display
 * - Single/double-click selection
 * - Visual feedback for selected layer
 *
 * The parent component (RegisterAssetPage) is responsible for:
 * - Managing form state updates
 * - Handling navigation to next step
 * - Setting composite/training layer flags
 */
const LayerSelectionStep: React.FC<LayerSelectionStepProps> = ({
  selectedLayerCode,
  onLayerSelect,
  disabled,
  error,
}) => {
  // If there's an error from parent, display it
  if (error) {
    return (
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            p: 2,
            bgcolor: 'error.light',
            color: 'error.contrastText',
            borderRadius: 1,
            mb: 2,
          }}
        >
          {error}
        </Box>
        <LayerSelection
          onLayerSelect={onLayerSelect}
          selectedLayerCode={selectedLayerCode}
        />
      </Box>
    );
  }

  // If disabled, show overlay
  if (disabled) {
    return (
      <Box sx={{ position: 'relative', opacity: 0.6, pointerEvents: 'none' }}>
        <LayerSelection
          onLayerSelect={onLayerSelect}
          selectedLayerCode={selectedLayerCode}
        />
      </Box>
    );
  }

  // Normal rendering
  return (
    <LayerSelection
      onLayerSelect={onLayerSelect}
      selectedLayerCode={selectedLayerCode}
    />
  );
};

export default LayerSelectionStep;
